import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import { Button, Modal, Input, Field, Select, Spinner, Card } from "./ui";
import { cn } from "../lib/utils";
import { Plus, Building2, Search, Trash2, Users, Briefcase, FileText, TrendingUp, ArrowUpRight, Globe, MapPin } from "lucide-react";

const fmtMoney = (v, loc) => v == null ? "—" : new Intl.NumberFormat(loc, { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v);

export default function CompaniesPanel() {
  const { t, lang, entities } = useApp();
  const loc = lang === "fr" ? "fr-FR" : "en-US";
  const [companies, setCompanies] = useState(null);
  const [q, setQ] = useState("");
  const [entityFilter, setEntityFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const [detailId, setDetailId] = useState(null);

  const load = useCallback(() => {
    api.get("/data/companies").then(({ data }) => setCompanies(data)).catch(() => setCompanies([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (companies === null) return <div className="py-12 flex justify-center"><Spinner className="w-6 h-6 text-text-secondary" /></div>;

  const entName = (id) => entities.find((e) => e.id === id)?.name;
  const entColor = (id) => entities.find((e) => e.id === id)?.color;
  const filtered = companies.filter((c) =>
    (c.name || "").toLowerCase().includes(q.toLowerCase()) &&
    (!entityFilter || (c.entity_ids || []).includes(entityFilter)));

  return (
    <div data-testid="companies-panel">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-text-secondary absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("companies_search")} className="pl-9" data-testid="companies-search" />
        </div>
        <Select value={entityFilter} onChange={(e) => setEntityFilter(e.target.value)} className="max-w-[200px]" data-testid="companies-entity-filter">
          <option value="">{t("companies_all_entities")}</option>
          {entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
        </Select>
        <Button onClick={() => setEditing({})} data-testid="company-new"><Plus className="w-4 h-4 mr-1.5" />{t("company_new")}</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="companies-grid">
        {filtered.map((c) => (
          <button key={c.id} onClick={() => setDetailId(c.id)} data-testid={`company-card-${c.id}`}
            className="text-left p-4 rounded-2xl border bg-surface hover:shadow-lg hover:-translate-y-0.5 transition-all group">
            <div className="flex items-start justify-between">
              <div className="w-9 h-9 rounded-xl bg-text-primary/10 flex items-center justify-center"><Building2 className="w-4.5 h-4.5 text-text-secondary" /></div>
              <ArrowUpRight className="w-4 h-4 text-text-secondary opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
            <p className="font-semibold mt-2.5">{c.name}</p>
            {c.industry && <p className="text-xs text-text-secondary mt-0.5">{c.industry}</p>}
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              {c.location && <span className="inline-flex items-center gap-1 text-[11px] text-text-secondary"><MapPin className="w-3 h-3" />{c.location}</span>}
              {(c.entity_ids || []).map((eid) => entName(eid) && (
                <span key={eid} className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-surface-hover">
                  <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: entColor(eid) }} />{entName(eid)}
                </span>
              ))}
            </div>
          </button>
        ))}
        {!filtered.length && <p className="text-sm text-text-secondary col-span-full py-8 text-center">{t("companies_empty")}</p>}
      </div>

      {editing && <CompanyForm company={editing} entities={entities} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {detailId && <CompanyDetail companyId={detailId} loc={loc} onClose={() => setDetailId(null)} onEdit={(c) => { setDetailId(null); setEditing(c); }} onDeleted={() => { setDetailId(null); load(); }} />}
    </div>
  );
}

function CompanyForm({ company, entities, onClose, onSaved }) {
  const { t, toast } = useApp();
  const [f, setF] = useState({
    name: company.name || "", domain: company.domain || "", industry: company.industry || "",
    location: company.location || "", notes: company.notes || "", entity_ids: company.entity_ids || [],
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const toggleEnt = (id) => set("entity_ids", f.entity_ids.includes(id) ? f.entity_ids.filter((x) => x !== id) : [...f.entity_ids, id]);
  const save = async () => {
    if (!f.name.trim()) { toast(t("company_name_required")); return; }
    if (company.id) await api.put(`/data/companies/${company.id}`, f);
    else await api.post("/data/companies", f);
    onSaved();
  };
  return (
    <Modal open onClose={onClose} title={company.id ? t("company_edit") : t("company_new")}
      footer={<><Button variant="outline" onClick={onClose}>{t("cancel")}</Button><Button onClick={save} disabled={!f.name.trim()} data-testid="company-save">{t("save")}</Button></>}>
      <div className="space-y-4">
        <Field label={t("crm_companies")}><Input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="company-name" autoFocus /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("company_website")}><Input value={f.domain} onChange={(e) => set("domain", e.target.value)} data-testid="company-domain" /></Field>
          <Field label={t("company_industry")}><Input value={f.industry} onChange={(e) => set("industry", e.target.value)} data-testid="company-industry" /></Field>
          <Field label={t("company_location")}><Input value={f.location} onChange={(e) => set("location", e.target.value)} data-testid="company-location" /></Field>
        </div>
        <div>
          <div className="text-xs text-text-secondary mb-1.5">{t("company_entities")}</div>
          <div className="flex flex-wrap gap-1.5" data-testid="company-entities">
            {entities.map((e) => (
              <button key={e.id} onClick={() => toggleEnt(e.id)} data-testid={`company-entity-${e.id}`}
                className={cn("inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs", f.entity_ids.includes(e.id) ? "border-primary bg-primary/10 text-text-primary" : "text-text-secondary")}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: e.color }} />{e.name}
              </button>
            ))}
          </div>
        </div>
        <Field label={t("company_notes")}><textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} rows={2} className="w-full rounded-lg border bg-surface px-3 py-2 text-sm outline-none" data-testid="company-notes" /></Field>
      </div>
    </Modal>
  );
}

function CompanyDetail({ companyId, loc, onClose, onEdit, onDeleted }) {
  const { t } = useApp();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/crm/companies/${companyId}/overview`).then(({ data }) => setD(data)).catch(() => setD({ error: true })); }, [companyId]);
  const del = async () => { await api.delete(`/data/companies/${companyId}`); onDeleted(); };

  const Section = ({ icon: Icon, title, count, children }) => (
    <div>
      <div className="flex items-center gap-2 mb-2 text-xs font-medium text-text-secondary uppercase tracking-wide"><Icon className="w-3.5 h-3.5" />{title} <span className="tabular-nums">{count}</span></div>
      {children}
    </div>
  );

  return (
    <Modal open onClose={onClose} title={d?.company?.name || t("crm_companies")}
      footer={d && !d.error ? <>
        <button onClick={del} data-testid="company-detail-delete" className="text-sm text-red-400 hover:text-red-300 mr-auto inline-flex items-center gap-1.5"><Trash2 className="w-4 h-4" />{t("delete")}</button>
        <Button variant="outline" onClick={() => onEdit(d.company)} data-testid="company-detail-edit">{t("edit")}</Button>
        <Button onClick={onClose}>{t("done") || "Done"}</Button>
      </> : <Button onClick={onClose}>{t("done") || "Done"}</Button>}>
      {!d ? <div className="py-10 flex justify-center"><Spinner className="w-6 h-6 text-text-secondary" /></div> :
        d.error ? <p className="text-sm text-text-secondary py-6 text-center">{t("companies_empty")}</p> : (
        <div className="space-y-5" data-testid="company-detail">
          <div className="flex flex-wrap gap-3 text-xs text-text-secondary">
            {d.company.domain && <span className="inline-flex items-center gap-1"><Globe className="w-3.5 h-3.5" />{d.company.domain}</span>}
            {d.company.industry && <span>{d.company.industry}</span>}
            {d.company.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{d.company.location}</span>}
          </div>

          <Section icon={Users} title={t("crm_people")} count={d.people.length}>
            {d.people.length ? <div className="space-y-1.5">{d.people.map((p) => (
              <div key={p.id} className="flex items-center gap-2 text-sm" data-testid={`company-person-${p.id}`}>
                <span className="font-medium">{p.name}</span>{p.title && <span className="text-text-secondary text-xs">· {p.title}</span>}
                {p.email && <span className="text-text-secondary text-xs ml-auto">{p.email}</span>}
              </div>))}</div> : <p className="text-xs text-text-secondary">—</p>}
          </Section>

          <Section icon={TrendingUp} title={t("nav_deals")} count={d.deals.length}>
            {d.deals.length ? <div className="space-y-1.5">{d.deals.map((dl) => (
              <button key={dl.id} onClick={() => dl.entity_key && navigate(`/entity/${dl.entity_key}?tab=deals`)} data-testid={`company-deal-${dl.id}`} className="w-full flex items-center gap-2 text-sm hover:bg-surface-hover px-1.5 py-1 rounded-md">
                {dl.stage_color && <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: dl.stage_color }} />}
                <span className="truncate">{dl.title}</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-surface-hover text-text-secondary shrink-0">{dl.entity_name}</span>
                <span className="text-text-secondary text-xs ml-auto tabular-nums shrink-0">{fmtMoney(dl.value, loc)}</span>
              </button>))}</div> : <p className="text-xs text-text-secondary">—</p>}
          </Section>

          <Section icon={FileText} title={t("res_invoices")} count={d.invoices.length}>
            {d.invoices.length ? <div className="space-y-1">{d.invoices.map((i) => <div key={i.id} className="text-sm flex items-center gap-2" data-testid={`company-invoice-${i.id}`}><span>{i.number}</span><span className="text-text-secondary text-xs ml-auto tabular-nums">{fmtMoney(i.amount, loc)}</span></div>)}</div> : <p className="text-xs text-text-secondary">—</p>}
          </Section>
        </div>
      )}
    </Modal>
  );
}
